import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import { LockedTransferNote } from '@/components/wms/LockedTransferNote';

// Destination location for Location to Location Transfer: scanned from the bin label, never picked from a list.
export const DestinationScanCard = ({ dest, onChange }) => {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (!dest) ref.current?.focus(); }, [dest]);

  const handleScan = async (e) => {
    if (e.key !== 'Enter') return;
    const raw = code.trim();
    if (!raw) return;
    setChecking(true);
    try {
      const v = await greigeStorageApi.validateGreigeLocation(raw, null, 'transfer');
      if (!v.valid) {
        toast.error(v.reason || `Location '${raw}' cannot be used`);
        return;
      }
      onChange({ location_id: v.location_id, footprint: v.footprint, occupant_design: v.occupant_design || '', lock: v.lock || null });
      toast.success(`Destination set: ${v.footprint}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check location');
    } finally {
      setCode('');
      setChecking(false);
    }
  };

  return (
    <Card className="border-[#E5DFD0] mb-6" data-testid="dest-location-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" />Destination Location</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {dest ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200" data-testid="dest-location-chip">
            <div className="flex items-center gap-2 min-w-0">
              <Check className="w-4 h-4 text-green-600 shrink-0" />
              <div className="min-w-0">
                <p className="font-mono font-semibold text-[#1A1F2E] truncate" data-testid="dest-location-footprint">{dest.footprint}</p>
                <p className="text-xs text-[#5A5F6E]" data-testid="dest-location-occupant">
                  {dest.occupant_design ? `Holds ${dest.occupant_design}. Only this design can go here.` : 'Empty location'}
                </p>
                <LockedTransferNote lock={dest.lock} testId="dest-location-lock-note" />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => onChange(null)} data-testid="dest-location-change-btn">Change</Button>
          </div>
        ) : (
          <div>
            <Label>Scan Destination Location (Put Away To)</Label>
            <Input
              ref={ref}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={handleScan}
              placeholder={checking ? 'Checking location…' : 'Scan location label...'}
              disabled={checking}
              className="mt-1"
              data-testid="dest-location-scan-input"
            />
            <p className="text-xs text-[#8A8F9E] mt-2">
              Scan the destination first. Each scanned bundle moves from its current location automatically.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
